import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { cn } from '../../utils';
import { usePagination } from './usePagination';
import { Fragment, ReactNode } from 'react';
import Results from './Results';

type PaginationProps = {
    totalItems: number;
    showResults?: boolean;
    perPage?: number;
    startPage?: number;
    currentPage?: number;
    numSlots?: number;
    border?: boolean;
    children: ({
        className,
        pageParam,
        children,
    }: PaginationChildrenProps) => JSX.Element;
};

export type PaginationChildrenProps = {
    className: string;
    pageParam: number | string;
    children: JSX.Element;
    key?: string;
};

export default function Pagination({
    totalItems,
    children,
    showResults = true,
    perPage = 10,
    startPage = 1,
    currentPage = 1,
    numSlots = 7,
    border = true,
}: PaginationProps) {
    const paginate = usePagination({
        startPage,
        currentPage,
        numSlots,
        totalItems,
        perPage,
    });

    return (
        <div
            data-testid="pagination"
            className={cn(
                'alli-flex alli-items-center alli-bg-white alli-px-4 alli-py-3 sm:alli-px-6',
                {
                    'alli-border-t alli-border-gray-400': border,
                },
            )}>
            <div
                className={cn('alli-flex alli-flex-1 sm:alli-items-center', {
                    'alli-justify-center md:alli-justify-between': showResults,
                    'alli-justify-center': !showResults,
                })}>
                {showResults && (
                    <Results
                        totalItems={totalItems}
                        perPage={perPage}
                        currentPage={currentPage}
                    />
                )}

                <div>
                    <nav
                        data-testid="pagination-nav"
                        className="alli-isolate alli-inline-flex -alli-space-x-px alli-rounded-md alli-shadow-sm"
                        aria-label="Pagination">
                        <RelativeLink
                            as={children}
                            disabled={currentPage === startPage}
                            type="prev"
                            page={currentPage - 1}>
                            <ChevronLeftIcon
                                className="alli-h-5 alli-w-5"
                                aria-hidden="true"
                            />
                        </RelativeLink>
                        {paginate.links.map((item, index) => {
                            const fillItem = item === '...';
                            const elProps = {
                                pageParam: item,
                                className: cn(
                                    'alli-relative alli-inline-flex alli-items-center alli-px-4 alli-py-2 alli-text-sm alli-font-medium focus:alli-z-20',
                                    {
                                        'hover:alli-bg-gray-100': !fillItem,
                                        'alli-z-10 alli-bg-blue-600 alli-text-white hover:alli-bg-blue-600 focus-visible:alli-outline focus-visible:alli-outline-2 focus-visible:alli-outline-offset-2 focus-visible:alli-outline-blue-600':
                                            item === currentPage,
                                        'alli-text-blue-gray-700 alli-ring-1 alli-ring-inset alli-ring-gray-400  focus:alli-outline-offset-0':
                                            item !== currentPage,
                                        'alli-text-blue-gray-700': fillItem,
                                    },
                                ),
                                children: <>{item}</>,
                                key: `pagination-${index}`,
                            };

                            if (fillItem) {
                                return (
                                    <span
                                        key={elProps.key}
                                        className={elProps.className}>
                                        {elProps.children}
                                    </span>
                                );
                            }

                            return (
                                <Fragment key={elProps.key}>
                                    {children({ ...elProps })}
                                </Fragment>
                            );
                        })}
                        <RelativeLink
                            as={children}
                            type="next"
                            disabled={currentPage === paginate.totalPages}
                            page={currentPage + 1}>
                            <ChevronRightIcon
                                className="alli-h-5 alli-w-5"
                                aria-hidden="true"
                            />
                        </RelativeLink>
                    </nav>
                </div>
            </div>
        </div>
    );
}

type RelativeLinkProps = {
    type: 'next' | 'prev';
    children: ReactNode;
    disabled: boolean;
    page: number;
    as: ({
        className,
        pageParam,
        children,
    }: PaginationChildrenProps) => JSX.Element;
};

function RelativeLink({
    type,
    children,
    disabled,
    as,
    page,
}: RelativeLinkProps) {
    const elProps = {
        className: cn(
            'alli-relative alli-inline-flex alli-items-center alli-px-2 alli-py-2 alli-text-blue-gray-700 alli-ring-1 alli-ring-inset alli-ring-gray-400 hover:alli-bg-gray-100 focus:alli-z-20 focus:alli-outline-offset-0',
            {
                'alli-rounded-r-md': type === 'next',
                'alli-rounded-l-md': type === 'prev',

                'alli-text-gray-500': disabled,
            },
        ),
        children: (
            <>
                <span className="alli-sr-only">
                    {type === 'next' ? 'Next' : 'Previous'}
                </span>
                {children}
            </>
        ),
        pageParam: page,
        key: `pagination-${children}`,
    };
    if (disabled) {
        return (
            <span key={elProps.key} className={elProps.className}>
                {elProps.children}
            </span>
        );
    }
    return as({ ...elProps });
}
